// hooks/useAssessmentState.ts
import { useEffect, useState, useCallback } from "react";
import type {AssessmentState, StepState, Turn} from "../models/assessment";
import type { StepId } from "../models/steps";
import {STEPS} from "../models/steps";


interface UseAssessmentStateResult {
    state: AssessmentState | null;
    loading: boolean;
    error: string | null;
    updateStepState: (stepId: StepId, updater: (prev: StepState) => StepState) => void;
    appendTurn: (stepId: StepId, turn: Turn) => void;
    markCompleted: (stepId: StepId) => void;
}

export function makeInitialAssessmentState(): AssessmentState {
    return STEPS.reduce((acc, step) => {
        acc[step.id] = {
            planned: true,     // or pre-fill from DB
            completed: false,
            history: []
        };
        return acc;
    }, {} as AssessmentState);
}

export function useAssessmentState(assessmentId: string): UseAssessmentStateResult {
    // 💡 purely local, for now
    const [state, setState] = useState<AssessmentState>(() => makeInitialAssessmentState());
    const [error, setError] = useState<string | null>(null);

    const loading = false; // no async work right now

    const updateStepState = useCallback(
        (stepId: StepId, updater: (prev: StepState) => StepState) => {
            setState(prev => ({
                ...prev,
                [stepId]: updater(prev[stepId]),
            }));
        },
        []
    );

    const appendTurn = useCallback(
        (stepId: StepId, turn: Turn) => {
            updateStepState(stepId, prev => ({
                ...prev,
                history: [...prev.history, turn],
            }));
        },
        [updateStepState]
    );

    const markCompleted = useCallback(
        (stepId: StepId) => {
            updateStepState(stepId, prev => ({
                ...prev,
                completed: true,
            }));
        },
        [updateStepState]
    );

    return { state, loading, error, updateStepState, appendTurn, markCompleted };
}

export function useAssessmentState2(assessmentId: string): UseAssessmentStateResult {
    const [state, setState] = useState<AssessmentState | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // 1️⃣ Load from backend on mount / id change
    useEffect(() => {
        let cancelled = false;

        async function load() {
            setLoading(true);
            setError(null);
            try {
                const res = await fetch(`/api/assessments/${assessmentId}`);
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json(); // { state: AssessmentState }

                if (!cancelled) {
                    setState(data.state);
                }
            } catch (err: any) {
                if (!cancelled) {
                    setError(err.message ?? "Failed to load assessment");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        load();
        return () => {
            cancelled = true;
        };
    }, [assessmentId]);

    // 2️⃣ Push updated state back to backend (simple version: every change)
    const saveState = useCallback(
        async (next: AssessmentState) => {
            // You can debounce/throttle this later
            try {
                await fetch(`/api/assessments/${assessmentId}`, {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ state: next }),
                });
            } catch {
                // optional: setError, show toast, etc.
            }
        },
        [assessmentId]
    );

    const updateStepState = useCallback(
        (stepId: StepId, updater: (prev: StepState) => StepState) => {
            setState(prev => {
                if (!prev) return prev;
                const next: AssessmentState = {
                    ...prev,
                    [stepId]: updater(prev[stepId]),
                };
                // optimistic save
                saveState(next);
                return next;
            });
        },
        [saveState]
    );

    const appendTurn = useCallback(
        (stepId: StepId, turn: Turn) => {
            updateStepState(stepId, prev => ({
                ...prev,
                history: [...prev.history, turn],
            }));
        },
        [updateStepState]
    );

    const markCompleted = useCallback(
        (stepId: StepId) => {
            updateStepState(stepId, prev => ({
                ...prev,
                completed: true,
            }));
        },
        [updateStepState]
    );

    return { state, loading, error, updateStepState, appendTurn, markCompleted };
}
